import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { userAPI, socialAPI, resolveMediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Loader2, MapPin, Trophy, Star, Wrench, MessageSquare, ArrowLeft, UserPlus, UserCheck, Heart } from "lucide-react";
import MarketingTopBar from "@/components/MarketingTopBar";
import ProfileAchievements from "@/components/ProfileAchievements";
import Layout from "@/components/Layout";
import { memberCategoryLabel, memberCategoryBadgeClass } from "@/lib/memberCategory";

const EQUIP_LABELS = {
  wheel_base: "Wheel Base",
  steering_wheel: "Steering Wheel",
  pedals: "Pedals",
  cockpit: "Cockpit",
  motion_system: "Motion System",
};

export default function PublicProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    userAPI.getBySlug(slug)
      .then((r) => {
        if (!alive) return;
        setProfile(r.data);
        setFollowerCount(Array.isArray(r.data?.followers) ? r.data.followers.length : (r.data?.followers_count || 0));
        if (currentUser?.id && Array.isArray(r.data?.followers)) {
          setIsFollowing(r.data.followers.includes(currentUser.id));
        }
        // Load their historical posts
        if (r.data?.id) {
          setPostsLoading(true);
          userAPI.getUserPosts(r.data.id)
            .then((pr) => { if (alive) setPosts(Array.isArray(pr.data) ? pr.data : pr.data?.posts || []); })
            .catch(() => {})
            .finally(() => { if (alive) setPostsLoading(false); });
        }
      })
      .catch(() => { if (alive) setNotFound(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug, currentUser?.id]);

  const toggleFollow = async () => {
    if (!currentUser) { navigate("/login"); return; }
    setFollowBusy(true);
    try {
      const res = await socialAPI.toggleFollow(profile.id);
      setIsFollowing(!!res.data?.following);
      setFollowerCount(res.data?.followers_count ?? followerCount);
      toast.success(res.data?.following ? `Following ${profile.username}` : "Unfollowed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update follow");
    } finally {
      setFollowBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (notFound || !profile) {
    const notFoundBody = (
      <div className="mx-auto max-w-xl px-6 pt-24 text-center">
        <h1 className="font-display text-3xl font-bold uppercase">Profile not found</h1>
        <p className="mt-3 text-sm text-muted-foreground">The URL <code className="rounded bg-secondary px-1.5 py-0.5">/u/{slug}</code> isn&apos;t claimed by any racer.</p>
        <Button variant="outline" className="mt-6" onClick={() => navigate("/")} data-testid="public-profile-home">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back home
        </Button>
      </div>
    );
    if (currentUser) return <Layout>{notFoundBody}</Layout>;
    return (
      <div className="min-h-screen bg-background">
        <MarketingTopBar compact />
        {notFoundBody}
      </div>
    );
  }

  const isOwn = currentUser?.id === profile.id;
  const equip = profile.equipment || {};
  const hasEquip = Object.values(equip).some((v) => v);

  const body = (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="public-profile-page">
      <Card className="relative min-h-[200px] overflow-hidden border-border p-0">
          {profile.cover_image ? (
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${resolveMediaUrl(profile.cover_image)})` }} />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 via-zinc-900 to-black" />
          )}
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
          <div className="relative flex flex-col items-start gap-4 p-6 pt-20 sm:flex-row sm:items-end">
            <Avatar className="h-24 w-24 border-2 border-primary">
              <AvatarImage src={profile.profile_image} />
              <AvatarFallback className="bg-secondary text-3xl">{(profile.username || "U").slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <h1 className="mt-1 truncate font-display text-3xl font-bold uppercase">{profile.username}</h1>
              {(profile.member_category && memberCategoryLabel(profile.member_category)) || profile.is_founding_member ? (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {profile.member_category && memberCategoryLabel(profile.member_category) && (
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${memberCategoryBadgeClass(profile.member_category)}`} data-testid="public-profile-member-badge">
                      {memberCategoryLabel(profile.member_category)}
                    </span>
                  )}
                  {profile.is_founding_member && profile.member_category !== "founding_member" && (
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${memberCategoryBadgeClass("founding_member")}`} data-testid="public-profile-founding-badge">
                      Founding Member
                    </span>
                  )}
                </div>
              ) : null}
              {profile.location && (
                <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3" /> {profile.location}
                </p>
              )}
              {profile.bio && <p className="mt-3 max-w-lg text-sm text-foreground/80">{profile.bio}</p>}
            </div>
            {isOwn ? (
              <Link to="/profile"><Button variant="outline" data-testid="public-profile-edit">Edit profile</Button></Link>
            ) : currentUser ? (
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  variant={isFollowing ? "outline" : "default"}
                  onClick={toggleFollow}
                  disabled={followBusy}
                  className={isFollowing ? "" : "bg-primary text-primary-foreground hover:bg-primary/90"}
                  data-testid="public-profile-follow"
                >
                  {followBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : isFollowing ? <><UserCheck className="mr-2 h-4 w-4" /> Following</> : <><UserPlus className="mr-2 h-4 w-4" /> Follow</>}
                </Button>
                <Link to={`/messages?user=${profile.id}`}>
                  <Button variant="outline" data-testid="public-profile-message">
                    <MessageSquare className="mr-2 h-4 w-4" /> Message
                  </Button>
                </Link>
              </div>
            ) : (
              <Link to="/login"><Button variant="outline" data-testid="public-profile-signin">Sign in to follow</Button></Link>
            )}
          </div>
        </Card>

        <div className="grid grid-cols-3 gap-3">
          <Card className="border-border bg-card p-4 text-center">
            <Trophy className="mx-auto mb-1 h-5 w-5 text-primary" />
            <p className="font-display text-xl font-bold">{profile.points ?? 0}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Points</p>
          </Card>
          <Card className="border-border bg-card p-4 text-center" data-testid="public-profile-followers">
            <UserCheck className="mx-auto mb-1 h-5 w-5 text-primary" />
            <p className="font-display text-xl font-bold">{followerCount}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Followers</p>
          </Card>
          <Card className="border-border bg-card p-4 text-center">
            <Star className="mx-auto mb-1 h-5 w-5 text-primary" />
            <p className="font-display text-xl font-bold">{profile.successful_sales ?? 0}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Sales</p>
          </Card>
        </div>

        {hasEquip && (
          <Card className="border-border bg-card p-5">
            <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold uppercase">
              <Wrench className="h-4 w-4 text-primary" /> Setup
            </h2>
            <div className="divide-y divide-border rounded-md border border-border">
              {Object.entries(EQUIP_LABELS).map(([k, label]) => (
                equip[k] ? (
                  <div key={k} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-medium">{equip[k]}</span>
                  </div>
                ) : null
              ))}
            </div>
          </Card>
        )}

        {profile?.id && <ProfileAchievements userId={profile.id} />}

        {/* Recent posts */}
        <Card className="border-border bg-card p-5" data-testid="public-profile-posts">
          <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold uppercase">
            Recent posts
          </h2>
          {postsLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : posts.length ? (
            <div className="space-y-3">
              {posts.slice(0, 12).map((p) => (
                <div key={p.id} className="rounded-md border border-border bg-secondary/30 p-3" data-testid={`public-profile-post-${p.id}`}>
                  <p className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</p>
                  {p.content && <p className="mt-1 text-sm">{p.content}</p>}
                  {p.images?.length > 0 && <img src={p.images[0]} alt="" className="mt-2 max-h-64 w-full rounded-md object-cover" />}
                  <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Heart className="h-3 w-3" /> {p.likes?.length || 0}</span>
                    <span>· {p.comment_count || 0} comments</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No posts yet.</p>
          )}
        </Card>
      </div>
  );

  if (currentUser) {
    return <Layout>{body}</Layout>;
  }

  return (
    <div className="min-h-screen bg-background">
      <MarketingTopBar compact />
      <div className="mx-auto max-w-3xl px-6 py-10">
        {body}
      </div>
    </div>
  );
}
